// src/pages/CarListingPage.tsx

import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Stack,
  CircularProgress,
  Pagination,
  Chip,
} from '@mui/material';
import CarCard from '../components/CarCard';
import CarFilterBar from '../components/CarFilterBar';
import axiosInstance from '../services/axiosInstance';
// ✅ Import decoder
import { decodeId } from '../utils/idEncoder';

interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const CarListingPage = ({ mode, setMode }: HomePageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // --- PARSING PARAMS (UPDATED) ---
  // We identify which keys are ID lists that need decoding
  const idListKeys = [
    'vehicleTransmissionTypeIds',
    'vehicleFuelTypeIds',
    'vehicleBrandIds',
    'vehicleBodyTypeIds',
    'vehicleClassIds',
    'dealerIds'
  ];

  const parseQueryParam = (key: string): number[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];
    
    // Split by comma
    const parts = raw.split(',');

    // If it's a known ID key, DECODE it. Otherwise, parse as int.
    if (idListKeys.includes(key)) {
      return parts
        .map(hash => decodeId(hash)) // Decode "X9k" -> 15
        .filter((id): id is number => id !== null); // Filter out invalids
    }

    return parts.map(Number);
  };

  const vehicleTransmissionTypeIds = parseQueryParam('vehicleTransmissionTypeIds');
  const vehicleFuelTypeIds = parseQueryParam('vehicleFuelTypeIds');
  const vehicleBrandIds = parseQueryParam('vehicleBrandIds');
  const vehicleBodyTypeIds = parseQueryParam('vehicleBodyTypeIds');
  const vehicleClassIds = parseQueryParam('vehicleClassIds');
  const dealerIds = parseQueryParam('dealerIds');

  // Status IDs are strings (e.g. "VSL"), so we DO NOT decode them
  const statusIdString = searchParams.get('statusId');
  const statusID = statusIdString ? statusIdString.split(',') : [];

  const parsePriceParams = () => {
    const min = searchParams.get('minAskingPrice');
    const max = searchParams.get('maxAskingPrice');
    return {
      minAskingPrice: min ? Number(min) : null,
      maxAskingPrice: max ? Number(max) : null,
    };
  };
  const { minAskingPrice, maxAskingPrice } = parsePriceParams();

  const parseYearParams = () => {
    const min = searchParams.get('minManufacturingYear');
    const max = searchParams.get('maxManufacturingYear');
    return {
      minManufacturingYear: min ? Number(min) : null,
      maxManufacturingYear: max ? Number(max) : null,
    };
  };
  const { minManufacturingYear, maxManufacturingYear } = parseYearParams();

  const parseMileageParams = () => {
    const min = searchParams.get('minMileage');
    const max = searchParams.get('maxMileage');
    return {
      minMileage: min ? Number(min) : null,
      maxMileage: max ? Number(max) : null,
    };
  };
  const { minMileage, maxMileage } = parseMileageParams();

  const parseDepreciationParams = () => {
    const min = searchParams.get('minDepreciation');
    const max = searchParams.get('maxDepreciation');
    return {
      minDepreciationValue: min ? Number(min) : null,
      maxDepreciationValue: max ? Number(max) : null,
    };
  };
  const { minDepreciationValue, maxDepreciationValue } = parseDepreciationParams();

  const sortKeys = searchParams.get('sortKeys')?.split(',') || [];
  const sortDirs = searchParams.get('sortDirs')?.split(',') as ('asc' | 'desc')[] || [];
  const sortList = sortKeys.map((key, i) => ({
    key,
    direction: sortDirs[i] || 'asc',
  }));

  const vehicleModel = searchParams.get('vehicleModel') || '';
  const initialSearchQuery = searchParams.get('searchKeyword') || '';

  // --- STATE ---
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [vehicleList, setVehicleList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [currentPage, setCurrentPage] = useState(1);
  const [racingAd, setRacingAd] = useState<boolean | null>(null);

  const itemsPerPage = viewMode === 'grid' ? 12 : 4;

  // --- FILTERED LIST LOGIC ---
  const filteredList = vehicleList
    .filter(vehicle => {
      const brandModel = `${vehicle.vehicleBrand} ${vehicle.vehicleModel || ''}`.toLowerCase();
      const matchesSearchQuery = brandModel.includes(searchQuery.toLowerCase());
      const matchesVehicleModel =
        !vehicleModel || brandModel.includes(vehicleModel.toLowerCase());

      return matchesSearchQuery && matchesVehicleModel;
    })
    .sort((a, b) => {
      for (const { key, direction } of sortList) {
        let aVal = a[key];
        let bVal = b[key];

        if (typeof aVal === 'string' && !isNaN(Number(aVal))) aVal = Number(aVal);
        if (typeof bVal === 'string' && !isNaN(Number(bVal))) bVal = Number(bVal);

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal !== bVal) return direction === 'asc' ? aVal - bVal : bVal - aVal;
          continue;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const result = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
          if (result !== 0) return direction === 'asc' ? result : -result;
          continue;
        }

        const result = String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, {
          sensitivity: 'base',
        });
        if (result !== 0) return direction === 'asc' ? result : -result;
      }
      return 0;
    });

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const paginatedList = filteredList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        const payload: any = {
          vehicleTransmissionTypeIds,
          vehicleFuelTypeIds,
          vehicleBrandIds,
          vehicleClassIds,
          vehicleBodyTypeIds,
          minAskingPrice,
          maxAskingPrice,
          minManufacturingYear,
          maxManufacturingYear,
          minMileage,
          maxMileage,
          minDepreciationValue,
          maxDepreciationValue,
          statusID,
          dealerIds,
        };

        if (racingAd) {
          payload.racingAd = true;
        }

        const response = await axiosInstance.post(
          "/CustomerVehicle/VehicleGetListPublished",
          payload,
        );

        const result = response.data as {
          responseCode: string;
          responseMessage?: string;
          responseData?: { vehicleList?: any[] };
        };

        if (result.responseCode === "00" && result.responseData) {
          setVehicleList(result.responseData.vehicleList || []);
        } else {
          console.error("Failed to load vehicles:", result.responseMessage);
          setVehicleList([]);
        }
      } catch (err) {
        console.error("Error fetching vehicles", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [location.search, racingAd]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, viewMode, location.search]);

  // --- HANDLER: FILTER CHANGE (UPDATED) ---
  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);
    
    // Logic: If value provided, OVERWRITE existing. If empty, DELETE.
    if (value) {
      currentParams.set(key, value);
    } else {
      currentParams.delete(key);
    }

    currentParams.delete('page'); // Always reset pagination
    navigate(`/vehicle-listing?${currentParams.toString()}`);
  };

  const vehicleClassMap: Record<number, string> = {
    2: 'Light Commercial',
    3: 'Heavy Commercial',
  };

  const handleRemoveVehicleClass = (idToRemove: number) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get('vehicleClassIds');

    if (!existing) return;

    // We must DECODE the existing URL params to find the one to remove
    const parts = existing.split(',');
    
    // Filter out the hash that corresponds to the idToRemove
    const updatedParts = parts.filter(hash => {
        const decoded = decodeId(hash);
        return decoded !== idToRemove;
    });

    if (updatedParts.length > 0) {
      currentParams.set('vehicleClassIds', updatedParts.join(','));
    } else {
      currentParams.delete('vehicleClassIds');
    }

    navigate(`/vehicle-listing?${currentParams.toString()}`);
  };

  return (
    <>
      <Header />
      <Box sx={{ px: 4, py: 6, mt: 5, minHeight: '80vh' }}>
        <Box display="flex" justifyContent="center">
          <Box sx={{ width: '100%', maxWidth: 1200 }}>
            
            {/* Class Filters Display */}
            {vehicleClassIds.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Showing vehicles from:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {vehicleClassIds.map((id) => (
                    <Box key={id}>
                      <Chip
                        label={vehicleClassMap[id] || `Class ${id}`}
                        onDelete={() => handleRemoveVehicleClass(id)}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Filter Bar */}
            <CarFilterBar
              viewMode={viewMode}
              onToggleViewMode={() => setViewMode(prev => (prev === 'grid' ? 'list' : 'grid'))}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onFilterChange={handleFilterChange}
            />

            {/* Racing Ads Toggle */}
            <Box mb={2} display="flex" gap={2}>
              {racingAd ? (
                <Chip
                  label="Racing Ads"
                  color="primary"
                  onDelete={() => setRacingAd(null)}
                />
              ) : (
                <Chip
                  label="Show Racing Ads only"
                  clickable
                  color="default"
                  onClick={() => setRacingAd(true)}
                />
              )}
            </Box>

            {/* Loading State */}
            {loading ? (
               <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            ) : (
              /* Content */
              <>
                {viewMode === 'grid' ? (
                  <Stack spacing={2} mt={2}>
                    <Grid container spacing={2}>
                      {paginatedList.map((vehicle: any) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={vehicle.vehicleId}>
                          <CarCard vehicle={vehicle} viewMode="grid" />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                ) : (
                  <Stack spacing={2} mt={2}>
                    {paginatedList.map(vehicle => (
                      <CarCard
                        key={vehicle.vehicleId}
                        vehicle={vehicle}
                        viewMode="list"
                      />
                    ))}
                  </Stack>
                )}

                {/* Pagination */}
                {filteredList.length > 0 ? (
                  <Box
                    mt={4}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                      {Math.min(currentPage * itemsPerPage, filteredList.length)} of{' '}
                      {filteredList.length} Vehicle
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                    />
                  </Box>
                ) : (
                   <Box mt={4} textAlign="center">
                      <Typography color="text.secondary">No vehicles found.</Typography>
                   </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default CarListingPage;